import { z } from 'zod';
import { slugSchema } from './catalog.js';

export const pageKindSchema = z.enum(['PAGE', 'SERVICE']);
export type PageKind = z.infer<typeof pageKindSchema>;

export const pageStatusSchema = z.enum(['DRAFT', 'PUBLISHED', 'ARCHIVED']);

/**
 * Halaman bawaan di kode yang boleh ditimpa dari admin. Selama belum ada
 * versi terbit di admin, isi dari kode yang tampil.
 */
export const BUILTIN_PAGE_SLUGS = ['tentang', 'kontak', 'cara-pesan', 'kebijakan'] as const;

/**
 * Segmen pertama URL yang dipakai situs atau disiapkan untuknya. Halaman
 * (PAGE) tampil di root, jadi slug ini ditolak supaya tidak menutupi rute asli.
 */
export const RESERVED_PAGE_SLUGS = [
  'akun',
  'api',
  'artikel',
  'checkout',
  'daftar',
  'desain',
  'faq',
  'katalog',
  'keranjang',
  'layanan',
  'lupa-sandi',
  'masuk',
  'pesanan',
  'produk',
  'reset-sandi',
  'studio',
  'template',
  'admin',
  'sitemap',
  'robots',
  'opengraph-image',
  'favicon',
] as const;

/**
 * Penanda data toko yang boleh ditulis di isi halaman. Diganti di server
 * saat halaman dibaca, jadi mengubah nomor WA cukup di satu tempat.
 */
export const PAGE_TOKENS = [
  { token: '{{nama_toko}}', label: 'Nama toko' },
  { token: '{{nama_usaha}}', label: 'Nama badan usaha' },
  { token: '{{whatsapp}}', label: 'Nomor WhatsApp (tautan)' },
  { token: '{{email}}', label: 'Email (tautan)' },
  { token: '{{alamat}}', label: 'Alamat workshop' },
  { token: '{{jam_buka}}', label: 'Jam buka' },
  { token: '{{peta}}', label: 'Tautan Google Maps' },
] as const;

/**
 * Field mentah tanpa default: .partial() tidak membatalkan .default(), jadi
 * schema ubah dibuat dari sini supaya PATCH parsial tidak mengisi nilai bawaan.
 */
const pageFields = {
  title: z.string().trim().min(3, 'Judul minimal 3 karakter').max(180),
  excerpt: z.string().trim().max(400).nullable(),
  contentJson: z.record(z.string(), z.unknown()),
  contentHtml: z.string().min(1).max(400_000),
  status: pageStatusSchema,
  sortOrder: z.number().int().min(0).max(100_000),
  seoTitle: z.string().trim().max(180).nullable(),
  seoDescription: z.string().trim().max(320).nullable(),
  coverMediaId: z.string().max(64).nullable(),
  priceNote: z.string().trim().max(120).nullable(),
  ctaLabel: z.string().trim().max(60).nullable(),
  ctaMessage: z.string().trim().max(500).nullable(),
};

export const pageInputSchema = z.object({
  slug: slugSchema,
  ...pageFields,
  excerpt: pageFields.excerpt.default(null),
  contentJson: pageFields.contentJson.default({}),
  status: pageFields.status.default('DRAFT'),
  sortOrder: pageFields.sortOrder.default(0),
  seoTitle: pageFields.seoTitle.default(null),
  seoDescription: pageFields.seoDescription.default(null),
  coverMediaId: pageFields.coverMediaId.default(null),
  priceNote: pageFields.priceNote.default(null),
  ctaLabel: pageFields.ctaLabel.default(null),
  ctaMessage: pageFields.ctaMessage.default(null),
});

export const pageUpdateSchema = z
  .object(pageFields)
  .partial()
  .refine((data) => Object.keys(data).length > 0, { message: 'Tidak ada perubahan yang dikirim' });

export const pageParamsSchema = z.object({ slug: slugSchema });

export const adminPageQuerySchema = z.object({
  status: pageStatusSchema.optional(),
});

export type PageInput = z.infer<typeof pageInputSchema>;
export type PageUpdate = z.infer<typeof pageUpdateSchema>;
export type AdminPageQuery = z.infer<typeof adminPageQuerySchema>;
